<ng-template let-extraClass="extraClass" #topNav>
  @if (!windowService.isDesktopRes) {
    <tidy-top-nav
      [class]="extraClass"
      [title]="'Inquiries'"
      [canGoBack]="true"
      [customBack]="'more'" />
  }
</ng-template>
<ng-container *ngTemplateOutlet="topNav;context:{ extraClass: 'mobile' }" />

<ng-template #content>
  <tidy-wrapper [isPrimaryLayer]="true">

    <!-- Status filter menu. Lives in the page body rather than the header
         because mat-menu renders into an overlay; the header only holds the
         trigger, and both the desktop and mobile triggers point here. -->
    <mat-menu #statusMenu="matMenu" class="filter-menu">
      @for (item of statusFilterItems; track item.value) {
        <button mat-menu-item class="tree-node-padding">
          <mat-checkbox
            class="checkbox"
            [checked]="selectedStatus() === item.value"
            (click)="$event.stopPropagation()"
            (change)="onStatusFilterChange(item.value, $event.checked)">
            {{ item.viewValue | translate }}
          </mat-checkbox>
        </button>
      }
    </mat-menu>

    @if (windowService.isDesktopRes) {
      <tidy-desktop-header>
        <div class="wrappable-container allow-wrap">
          <tidy-text [title]="'Inquiries'" />
          @if (hasAnyInquiries()) {
            <form [formGroup]="searchForm" novalidate action="#">
              <tidy-search-input
                formControlName="search"
                [placeholder]="'Search'"
                [icon]="'assets/img/search-header.svg'"
                class="header-search" />
            </form>
          }
          @if (hasAnyInquiries() && distinctProperties().length > 1) {
            <form [formGroup]="searchForm" novalidate action="#">
              <tidy-header-select
                [items]="propertyFilterItems()"
                [form]="searchForm.get('selectedProperty')"
                formControlName="selectedProperty"
                [icon]="'assets/svg/home.svg'"
                (optionChange)="onPropertyFilterChange($event)" />
            </form>
          }
          @if (hasAnyInquiries()) {
            <tidy-small-button
              [text]="'Filter'"
              [image]="'assets/img/filter-white.svg'"
              [matMenuTriggerFor]="statusMenu" />
            @if (statusChipLabel(); as chipLabel) {
              <ion-chip class="filter-chip">
                <mat-icon class="chip-icon">flag</mat-icon>
                <ion-label>{{ chipLabel | translate }}</ion-label>
                <ion-icon name="close-circle" (click)="clearStatusFilter()" />
              </ion-chip>
            }
          }
        </div>
        <div class="non-wrappable-right-aligned-container">
          <automations-preview-button
            [tag]="'inquiries'" />
        </div>
      </tidy-desktop-header>
    }

    @if (!windowService.isDesktopRes && hasAnyInquiries()) {
      <div class="inq-mobile-filters">
        <div class="mobile-filter-row">
          <form [formGroup]="searchForm" novalidate action="#" class="inq-mobile-search">
            <tidy-search-input
              formControlName="search"
              [placeholder]="'Search'"
              [icon]="'assets/svg/search.svg'"
              [fixedWidth]="false"
              class="header-search" />
          </form>
          <tidy-small-button
            class="mobile-filter-button"
            [iconOnly]="true"
            [image]="'assets/img/filter-white.svg'"
            [matMenuTriggerFor]="statusMenu" />
        </div>
        @if (distinctProperties().length > 1) {
          <form [formGroup]="searchForm" novalidate action="#">
            <tidy-header-select
              [items]="propertyFilterItems()"
              [form]="searchForm.get('selectedProperty')"
              formControlName="selectedProperty"
              [icon]="'assets/svg/home.svg'"
              (optionChange)="onPropertyFilterChange($event)" />
          </form>
        }
        @if (statusChipLabel(); as chipLabel) {
          <div class="mobile-filter-chips">
            <ion-chip class="filter-chip">
              <mat-icon class="chip-icon">flag</mat-icon>
              <ion-label>{{ chipLabel | translate }}</ion-label>
              <ion-icon name="close-circle" (click)="clearStatusFilter()" />
            </ion-chip>
          </div>
        }
      </div>
    }

    @if (isLoading()) {
      @if (windowService.isDesktopRes) {
        <tidy-table
          [skeletonLoading]="true"
          [skeletonRowCount]="4"
          [columnslabels]="tableHeaders" />
      } @else {
        <div class="inq-grid">
          @for (card of skeletonCards; track card) {
            <tidy-card class="inq-card inq-skeleton">
              <div class="inq-card__body">
                <div class="inq-head">
                  <ion-skeleton-text animated="true" class="inq-sk inq-sk--avatar" />
                  <div class="inq-head__meta">
                    <ion-skeleton-text animated="true" class="inq-sk inq-sk--title" />
                    <ion-skeleton-text animated="true" class="inq-sk inq-sk--sub" />
                  </div>
                </div>
                <div class="inq-pills">
                  <ion-skeleton-text animated="true" class="inq-sk inq-sk--pill" />
                  <ion-skeleton-text animated="true" class="inq-sk inq-sk--pill" />
                  <ion-skeleton-text animated="true" class="inq-sk inq-sk--pill" />
                </div>
                <ion-skeleton-text animated="true" class="inq-sk inq-sk--message" />
              </div>
            </tidy-card>
          }
        </div>
      }
    } @else if (!hasAnyInquiries()) {
      <div class="inq-empty">
        <tidy-image [src]="'assets/svg/calendar-outline.svg'" class="inq-empty__icon" />
        <tidy-text [header]="'No inquiries yet'" />
        <tidy-text
          [body]="'When a guest requests to book on any of your channels, their inquiry and its status will show up here.'"
          class="inq-empty__hint" />
      </div>
    } @else if (inquiries().length === 0) {
      <div class="inq-empty">
        <tidy-image [src]="'assets/svg/search-outline.svg'" class="inq-empty__icon" />
        <tidy-text [header]="'No matching inquiries'" />
        <tidy-text
          [body]="'Try a different search, or clear the status and property filters.'"
          class="inq-empty__hint" />
      </div>
    } @else {
      <!-- tidy-table is hidden below the desktop breakpoint by the shared
           component itself, so the card list is the mobile rendering of this
           same list — not a second view the host can switch to. -->
      @if (windowService.isDesktopRes) {
        <tidy-table
          [dataSource]="tableRows()"
          [displayedColumns]="tableHeaders"
          [columnslabels]="tableHeaders"
          [showColumnLines]="false"
          [rowsToShow]="50"
          [skipEntrance]="true"
          [allRowIsClickable]="true"
          [infiniteScroll]="true"
          [totalRows]="tableRows().length"
          (cellClick)="onRowClick($event)"
          (sortChange)="sortChanged($event)" />
      } @else {
        <div class="inq-grid">
          @for (inquiry of inquiries(); track inquiry.id) {
            <tidy-card class="inq-card inq-card--clickable" (click)="openInquiryCard(inquiry)">
              <div class="inq-card__body">
                <div class="inq-head">
                  <tidy-initials-icon
                    [fullName]="inquiry.guestName"
                    [size]="'large'"
                    class="inq-avatar" />
                  <div class="inq-head__meta">
                    <tidy-text [title]="inquiry.guestName" />
                    <tidy-text [helper]="inquiry.propertyLine" />
                  </div>
                  <tidy-image [src]="'assets/img/chevron-right.svg'" class="inq-card__chevron" />
                </div>

                <div class="inq-pills">
                  <span class="inq-source">
                    @if (inquiry.sourceLogo) {
                      <span [class]="'inq-source__avatar inq-source__avatar--' + inquiry.source">
                        <img [src]="inquiry.sourceLogo" [alt]="inquiry.sourceName" />
                      </span>
                    }
                    {{ inquiry.sourceName }}
                  </span>
                  <span class="inq-status">
                    <span class="inq-status__label">
                      <span [class]="'inq-status__dot inq-status__dot--' + inquiry.statusTone"></span>
                      {{ inquiry.statusLabel }}
                    </span>
                    @if (inquiry.statusDetail) {
                      <span class="inq-status__detail">{{ inquiry.statusDetail }}</span>
                    }
                  </span>
                  <span class="inq-pill">
                    <tidy-image [src]="'assets/svg/calendar-outline.svg'" class="inq-pill__icon" />
                    {{ inquiry.dateRange }}
                  </span>
                  @if (inquiry.guestsLabel) {
                    <span class="inq-pill">
                      <tidy-image [src]="'assets/svg/people-outline.svg'" class="inq-pill__icon" />
                      {{ inquiry.guestsLabel }}
                    </span>
                  }
                  <span class="inq-pill">{{ inquiry.totalLabel }}</span>
                </div>

                @if (inquiry.message) {
                  <div class="inq-message">
                    <tidy-text [helper]="inquiry.message" />
                  </div>
                } @else {
                  <div class="inq-message inq-message--empty">
                    <tidy-text [helper]="'No special request'" />
                  </div>
                }
              </div>
            </tidy-card>
          }
        </div>
      }
    }
  </tidy-wrapper>
</ng-template>

<app-scroll-wrapper [isfullWidthContent]="true" [templateContent]="content" />
